import React, { useState } from 'react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { 
  BuildingOfficeIcon,
  MapPinIcon,
  PhoneIcon,
  ChevronLeftIcon,
  ChevronRightIcon
} from '@heroicons/react/24/outline';

const OurBuildings = () => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  
  const buildingImages = [
    {
      src: '/assets/images/home/main.jpg',
      alt: 'P.S. 11X Main Building - The Highbridge School',
      title: 'Main Building - The Highbridge School',
      description: 'Historic building housing grades 2-5'
    },
    {
      src: '/assets/images/home/satellite.jpg',
      alt: 'P.S. 11X Satellite - Early Childhood Building',
      title: 'Satellite - Early Childhood Building',
      description: 'Modern facility for 3K-1st Grade'
    }
  ];

  const nextImage = () => {
    setCurrentImageIndex((prevIndex) => 
      prevIndex === buildingImages.length - 1 ? 0 : prevIndex + 1
    );
  };

  const prevImage = () => {
    setCurrentImageIndex((prevIndex) => 
      prevIndex === 0 ? buildingImages.length - 1 : prevIndex - 1
    );
  };

  return (
    <main id="main-content" className="bg-white">
      <Navbar />

      {/* Header Section */}
      <section className="pt-32 pb-24 bg-[#630503] relative overflow-hidden">
        <div aria-hidden="true" className="pointer-events-none absolute inset-x-0 bottom-0 h-1/5 bg-gradient-to-b from-[#630503] to-white"></div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center">
            <div className="mb-16">
              <span className="inline-block bg-white/10 backdrop-blur-sm text-white px-6 py-3 text-sm font-medium uppercase tracking-[0.2em] mb-8 rounded-full">
                Our School
              </span>
              <h1 className="text-4xl lg:text-5xl xl:text-6xl font-light text-white mb-8 leading-[1.1] max-w-4xl mx-auto">
                Our <span className="font-bold">Two Buildings</span>
              </h1>
              <div className="w-24 h-px bg-white/40 mx-auto mb-8"></div>
              <p className="text-lg lg:text-xl leading-relaxed text-white/95 max-w-3xl mx-auto">
                Two buildings standing proudly together, serving our diverse learning community.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Image Slideshow Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-xl p-8 lg:p-12 shadow-xl border border-gray-100 relative overflow-hidden">
            <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-[#630503] via-[#8B4513] to-[#A0522D]"></div>
            
            <div className="relative">
              <div className="aspect-video bg-gray-100 rounded-lg overflow-hidden relative">
                <img
                  src={buildingImages[currentImageIndex].src}
                  alt={buildingImages[currentImageIndex].alt}
                  className="w-full h-full object-cover"
                />
                
                {/* Navigation Arrows */}
                <button
                  onClick={prevImage}
                  aria-label="Show previous building photo"
                  className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-white/80 hover:bg-white text-gray-800 p-2 rounded-full shadow-lg transition-all duration-300"
                >
                  <ChevronLeftIcon className="h-6 w-6" aria-hidden="true" />
                </button>

                <button
                  onClick={nextImage}
                  aria-label="Show next building photo"
                  className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-white/80 hover:bg-white text-gray-800 p-2 rounded-full shadow-lg transition-all duration-300"
                >
                  <ChevronRightIcon className="h-6 w-6" aria-hidden="true" />
                </button>
                
                {/* Image Info Overlay */}
                <div className="absolute bottom-0 left-0 right-0 bg-black/70 p-6">
                  <h2 className="text-white text-xl font-bold mb-2">
                    {buildingImages[currentImageIndex].title}
                  </h2>
                  <p className="text-white/90 text-sm">
                    {buildingImages[currentImageIndex].description}
                  </p>
                </div>
              </div>
              
              {/* Dots Indicator */}
              <div className="flex justify-center mt-6 space-x-2">
                {buildingImages.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentImageIndex(index)}
                    aria-label={`Show photo ${index + 1} of ${buildingImages.length}: ${buildingImages[index].title}`}
                    aria-current={index === currentImageIndex}
                    className={`w-3 h-3 rounded-full transition-all duration-300 ${
                      index === currentImageIndex 
                        ? 'bg-[#630503]' 
                        : 'bg-gray-300 hover:bg-gray-400'
                    }`}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Buildings Overview Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-light text-gray-900 mb-8">
              Two Buildings, <span className="font-bold text-[#630503]">One Community</span>
            </h2>
            <div className="w-24 h-px bg-[#630503]/40 mx-auto mb-8"></div>
          </div>

          <div className="bg-[#630503]/5 rounded-xl p-8 lg:p-12 shadow-xl border border-[#630503]/20">
            <div className="prose prose-lg max-w-none text-center">
              <p className="text-lg text-gray-700 leading-relaxed">
                Through the years, P.S. 11x has become 2 buildings that stand proudly together, side by side a quarter mile apart on Ogden Ave. The historic main building houses grades 2-5 and the early childhood satellite building, known as the "annex" houses 3K-1st Grade. Through public advocacy and community leadership, the early childhood building was built to support the needs of early childhood!
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Building Details Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Main Building */}
            <div className="bg-white rounded-xl p-8 shadow-xl border border-gray-100 relative overflow-hidden">
              <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-[#630503] to-[#8B4513]"></div>
              
              <div className="mb-8">
                <div className="w-14 h-14 bg-[#630503]/10 rounded-xl flex items-center justify-center mb-4">
                  <BuildingOfficeIcon className="h-8 w-8 text-[#630503]" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">P.S. 11x: The Highbridge School</h3>
                <div className="w-16 h-px bg-[#630503]/40 mb-6"></div>
              </div>

              <div className="space-y-4">
                <div className="flex items-start">
                  <MapPinIcon className="h-6 w-6 text-[#630503] mr-3 mt-1 flex-shrink-0" />
                  <div>
                    <p className="font-semibold text-gray-900">Main Building Address</p>
                    <p className="text-gray-600">1257 Ogden Avenue<br />The Bronx, NY 10452</p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <PhoneIcon className="h-6 w-6 text-[#630503] mr-3 mt-1 flex-shrink-0" />
                  <div>
                    <p className="font-semibold text-gray-900">Phone</p>
                    <p className="text-gray-600">(718) 681-7553</p>
                  </div>
                </div>

                <div className="bg-[#630503]/5 rounded-lg p-4 mt-6">
                  <p className="text-sm font-medium text-gray-900 mb-2">Serves Grades:</p>
                  <p className="text-gray-700">2nd - 5th Grade</p>
                </div>
              </div>
            </div>

            {/* Satellite Building */}
            <div className="bg-white rounded-xl p-8 shadow-xl border border-gray-100 relative overflow-hidden">
              <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-[#8B4513] to-[#A0522D]"></div>
              
              <div className="mb-8">
                <div className="w-14 h-14 bg-[#8B4513]/10 rounded-xl flex items-center justify-center mb-4">
                  <BuildingOfficeIcon className="h-8 w-8 text-[#8B4513]" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">P.S. 11X Satellite - Early Childhood Building</h3>
                <div className="w-16 h-px bg-[#8B4513]/40 mb-6"></div>
              </div>

              <div className="space-y-4">
                <div className="flex items-start">
                  <MapPinIcon className="h-6 w-6 text-[#8B4513] mr-3 mt-1 flex-shrink-0" />
                  <div>
                    <p className="font-semibold text-gray-900">Satellite Building Address</p>
                    <p className="text-gray-600">1399 Ogden Ave<br />The Bronx, NY 10452</p>
                  </div>
                </div>

                <div className="bg-[#8B4513]/5 rounded-lg p-4 mt-6">
                  <p className="text-sm font-medium text-gray-900 mb-2">Serves Grades:</p>
                  <p className="text-gray-700">3K - 1st Grade</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Leadership Team Section */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">Our Leadership Team</h2>
            <div className="w-24 h-px bg-[#630503]/40 mx-auto mb-8"></div>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Meet the dedicated leaders who guide our school community with passion and vision.
            </p>
          </div>

          {/* Principal */}
          <div className="bg-white rounded-xl p-8 lg:p-12 shadow-xl border border-gray-100 relative overflow-hidden mb-12">
            <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-[#630503] via-[#8B4513] to-[#A0522D]"></div>
            
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-center">
              {/* Principal Image */}
              <div className="lg:col-span-1 text-center">
                <div className="w-48 h-48 mx-auto rounded-full overflow-hidden shadow-lg mb-6">
                  <img 
                    src="/assets/images/ourbuildings/principal.png" 
                    alt="Daisy Joy-Rodrigo - Principal"
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                </div>
                <h3 className="text-2xl font-bold text-[#630503] mb-2">Daisy Joy-Rodrigo</h3>
                <p className="text-lg text-gray-600 font-medium">Principal</p>
              </div>
              
              {/* Principal Information */}
              <div className="lg:col-span-2">
                <div className="bg-[#630503]/5 rounded-lg p-6">
                  <p className="text-gray-700 leading-relaxed">
                    My name is Daisy Joy-Rodrigo and I am humbled and honored to serve the Highbridge community in this role. I believe in the brilliance that exists in the Bronx, as I was once part of this community. It has always been a childhood dream to return to the Bronx community that welcomed my immigrant parents as they started out life in America. I am back home in District 9! I look forward to meeting you all, and getting to know the community that makes up P.S. 11X.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Vice Principals */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Vice Principal 1 - Henrietta Sirleaf */}
            <div className="bg-white rounded-xl p-8 shadow-xl border border-gray-100 relative overflow-hidden">
              <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-[#8B4513] to-[#A0522D]"></div>
              
              <div className="text-center mb-6">
                <div className="w-32 h-32 mx-auto rounded-full overflow-hidden shadow-lg mb-4">
                  <img 
                    src="/assets/images/ourbuildings/assistant-principal-1.png" 
                    alt="Henrietta Sirleaf - Vice Principal"
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                </div>
                <h3 className="text-xl font-bold text-[#8B4513] mb-1">Henrietta Sirleaf</h3>
                <p className="text-gray-600 font-medium">Vice Principal</p>
              </div>
              
              <div className="bg-[#8B4513]/5 rounded-lg p-4">
                <p className="text-gray-700 text-sm leading-relaxed">
                  I am Ms. Sirleaf and as a lifelong educator, I believe that every day is an opportunity to learn something new, and our students can accomplish anything through perseverance, hard work, and a strong desire to make a positive impact on their society.
                </p>
              </div>
            </div>

            {/* Vice Principal 2 - Alejandra Urteaga */}
            <div className="bg-white rounded-xl p-8 shadow-xl border border-gray-100 relative overflow-hidden">
              <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-[#A0522D] to-[#630503]"></div>
              
              <div className="text-center mb-6">
                <div className="w-32 h-32 mx-auto rounded-full overflow-hidden shadow-lg mb-4">
                  <img 
                    src="/assets/images/ourbuildings/assistant-principal-2.png" 
                    alt="Alejandra Urteaga - Vice Principal"
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                </div>
                <h3 className="text-xl font-bold text-[#A0522D] mb-1">Alejandra Urteaga</h3>
                <p className="text-gray-600 font-medium">Vice Principal</p>
              </div>
              
              <div className="bg-[#A0522D]/5 rounded-lg p-4">
                <p className="text-gray-700 text-sm leading-relaxed">
                  My name is Alejandra Urteaga. I am a wife, mother, immigrant and educator. I am honored to be working at PS 11X Highbridge School and community. I am a believer that all children should have the opportunity and equity to receive a high-quality education. Let's collaborate and work together to make great things happen!
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </main>
  );
};

export default OurBuildings; 